import type {
  CsvAnalyzeResponse,
  CsvImportResponse,
  CsvParseResponse,
  ImportDataSourcePrefill,
} from '@shared/schemas';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import { logger } from '@/utils/logger';

import { buildImportRequest } from '../domain/build-import-request';
import { type DataSourceDraft, dataSourceFromPrefill } from '../domain/data-source';
import { dataSourceErrors, validateForAnalyze } from '../domain/file-import.validation';
import { toFileImportError } from '../domain/file-import-error';
import type { FileImportGateway } from '../infrastructure/file-import.gateway';
import { analyzeRequested, importRequested, parseRequested } from './file-import.actions';
import {
  selectColumnMapping,
  selectDataSource,
  selectFileName,
  selectImportName,
  selectIsEnriched,
  selectListId,
  selectOverwriteRows,
  selectParseResult,
} from './file-import.selectors';
import { fileImportActions } from './file-import.slice';

/**
 * File-import saga. All I/O goes through the injected `FileImportGateway`
 * (read from the middleware context, never the api client directly), so tests
 * drive every branch with the in-memory gateway.
 */

/** WORKER: upload + parse the file; adopt the auto-mapping on success. */
function* parseWorker(action: ReturnType<typeof parseRequested>) {
  const gateway: FileImportGateway = yield getContext('fileImportGateway');
  const file = action.payload;
  // Client mode (#749): no file before a complete data source. The dropzone is
  // closed until then; this holds even if a file reaches the saga anyway.
  const listId: string | null = yield select(selectListId);
  if (listId === null) {
    const dataSource: DataSourceDraft = yield select(selectDataSource);
    const errors = dataSourceErrors(dataSource);
    if (Object.keys(errors).length > 0) {
      yield put(fileImportActions.parseFailed({ kind: 'validation', fields: errors }));
      return;
    }
  }
  yield put(fileImportActions.parseStarted({ fileName: file.name }));
  try {
    const result = (yield call(gateway.parseFile, file)) as CsvParseResponse;
    // A file the backend could not read at all: surface the first error and
    // stay on the Upload step (parity with the original stepper).
    if (result.errors.length > 0 && result.row_count === 0) {
      yield put(
        fileImportActions.parseFailed({ kind: 'api', message: result.errors[0], status: 0 }),
      );
      return;
    }
    yield put(fileImportActions.parseSucceeded(result));
  } catch (e) {
    yield put(fileImportActions.parseFailed(toFileImportError(e, 'Failed to parse file')));
  }
}

/**
 * WORKER: once the form is reset, pre-fill the client import's data source
 * with the licence's last import (#749). Silent: without one, or when the call
 * fails, the field stays empty for the client to fill in.
 */
function* prefillDataSourceWorker(action: ReturnType<typeof fileImportActions.formInitialized>) {
  if (action.payload.listId !== null) return;
  const gateway: FileImportGateway = yield getContext('fileImportGateway');
  try {
    const prefill = (yield call(gateway.fetchImportDataSource)) as ImportDataSourcePrefill;
    const draft = dataSourceFromPrefill(prefill);
    if (draft) yield put(fileImportActions.dataSourcePrefilled(draft));
  } catch (e) {
    logger.warn('Could not pre-fill the import data source', e);
  }
}

/** WORKER: validate (client mode needs a name and a data source) then dry-run the mapped rows. */
function* analyzeWorker() {
  const gateway: FileImportGateway = yield getContext('fileImportGateway');
  const listId: string | null = yield select(selectListId);
  const importName: string = yield select(selectImportName);
  const dataSource: DataSourceDraft = yield select(selectDataSource);
  const validationError = validateForAnalyze({ listId, importName, dataSource });
  if (validationError) {
    yield put(fileImportActions.analyzeFailed(validationError));
    return;
  }

  const parseResult: CsvParseResponse | null = yield select(selectParseResult);
  if (!parseResult) return; // nothing uploaded yet; the UI never dispatches this
  const columnMapping: Record<string, string> = yield select(selectColumnMapping);

  yield put(fileImportActions.analyzeStarted());
  try {
    const result = (yield call(gateway.analyzeRows, {
      column_mapping: columnMapping,
      rows: parseResult.rows,
    })) as CsvAnalyzeResponse;
    yield put(fileImportActions.analyzeSucceeded(result));
  } catch (e) {
    yield put(fileImportActions.analyzeFailed(toFileImportError(e, 'Analysis failed')));
  }
}

/** WORKER: build the admin/client request and run the import. */
function* importWorker() {
  const gateway: FileImportGateway = yield getContext('fileImportGateway');
  const parseResult: CsvParseResponse | null = yield select(selectParseResult);
  if (!parseResult) return;

  const listId: string | null = yield select(selectListId);
  const importName: string = yield select(selectImportName);
  const fileName: string | null = yield select(selectFileName);
  const columnMapping: Record<string, string> = yield select(selectColumnMapping);
  const isEnriched: boolean = yield select(selectIsEnriched);
  const overwriteRows: number[] = yield select(selectOverwriteRows);
  const dataSource: DataSourceDraft = yield select(selectDataSource);

  yield put(fileImportActions.importStarted());
  try {
    const request = buildImportRequest({
      listId,
      importName,
      fileName: fileName ?? '',
      columnMapping,
      rows: parseResult.rows,
      isEnriched,
      overwriteRows,
      dataSource,
    });
    const result = (yield call(gateway.importRows, request)) as CsvImportResponse;
    yield put(fileImportActions.importSucceeded(result));
  } catch (e) {
    yield put(fileImportActions.importFailed(toFileImportError(e, 'Import failed')));
  }
}

/** WATCHERS. */
export function* fileImportSaga() {
  yield all([
    takeLatest(fileImportActions.formInitialized.type, prefillDataSourceWorker),
    takeLatest(parseRequested.type, parseWorker),
    takeLatest(analyzeRequested.type, analyzeWorker),
    takeLatest(importRequested.type, importWorker),
  ]);
}
